import type { LoginFormValuesValidated } from "@authentication/presentation/pages/login/form/login.form";
import type { PasswordRecoveryFormValuesValidated } from "@authentication/presentation/pages/login/form/password-recovery.form";
import { ToastManagerRepository } from "@core/domain/repositories/toast-manager.repository";
import type { SelectOption } from "@core/domain/types/select-option";
import { LoadLayoutStore } from "@core/presentation/components/feedback/load-layout/load-layout.store";
import type { LoginError } from "@iot-platform/authentication";
import {
    AuthenticationLoginUseCase,
    AuthenticationRequestMailToResetPasswordUseCase,
    InvalidCredentialsError,
    LoginBlockedError,
} from "@iot-platform/authentication";
import type { LanguageIsoCodeEnum, Nullable } from "@iot-platform/core";
import {
    BaseViewModel,
    LanguageGetAllUseCase,
    LanguageGetCurrentUseCase,
    LanguageUpdateUseCase,
} from "@iot-platform/core";
import { t } from "i18next";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

export type LoginSubmitError =
    | { type: "invalidCredentials" }
    | { type: "blocked"; blockedUntil: Nullable<Date> };

@injectable()
export class LoginPageViewModel extends BaseViewModel {
    @observable
    languages: SelectOption<LanguageIsoCodeEnum>[] = [];

    @observable
    loginError: Nullable<LoginSubmitError> = null;

    constructor(
        @inject(AuthenticationLoginUseCase)
        private readonly authenticationLoginUseCase: AuthenticationLoginUseCase,
        @inject(AuthenticationRequestMailToResetPasswordUseCase)
        private readonly authenticationRequestMailToResetPasswordUseCase: AuthenticationRequestMailToResetPasswordUseCase,
        @inject(LanguageGetAllUseCase)
        private readonly languageGetAllUseCase: LanguageGetAllUseCase,
        @inject(LanguageUpdateUseCase)
        private readonly languageUpdateUseCase: LanguageUpdateUseCase,
        @inject(LanguageGetCurrentUseCase)
        private readonly languageGetCurrentUseCase: LanguageGetCurrentUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override didMount(): void {
        this.initViewData();
    }

    initViewData(): void {
        this.fetchLanguages();
    }

    getCurrentLanguage(): LanguageIsoCodeEnum {
        return this.languageGetCurrentUseCase.execute();
    }

    async submitLogin(values: LoginFormValuesValidated): Promise<boolean> {
        this.setLoginError(null);
        LoadLayoutStore.start();

        const loginResult = await this.authenticationLoginUseCase.execute(
            values.email,
            values.password,
        );

        await this.languageUpdateUseCase.execute(values.language);

        LoadLayoutStore.finish();

        if (loginResult.isErr()) {
            this.showLoginError(loginResult.error);
            return false;
        }

        return true;
    }

    async submitPasswordRecovery(
        values: PasswordRecoveryFormValuesValidated,
    ): Promise<boolean> {
        LoadLayoutStore.start();

        const passwordRecoveryResult =
            await this.authenticationRequestMailToResetPasswordUseCase.execute(
                values.email,
            );

        LoadLayoutStore.finish();

        if (passwordRecoveryResult) {
            ToastManagerRepository.success(
                t("public:authentication.recovery.success"),
            );
        }

        return passwordRecoveryResult;
    }

    @action
    private setLoginError(loginError: Nullable<LoginSubmitError>): void {
        this.loginError = loginError;
    }

    private showLoginError(error: LoginError): void {
        if (error instanceof InvalidCredentialsError) {
            this.setLoginError({ type: "invalidCredentials" });
        } else if (error instanceof LoginBlockedError) {
            this.setLoginError({
                type: "blocked",
                blockedUntil: error.blockedUntil,
            });
        } else {
            ToastManagerRepository.error(error.message);
        }
    }

    @action
    private fetchLanguages(): void {
        runInAction(() => {
            this.languages = this.languageGetAllUseCase.execute();
        });
    }
}
